// Utilitaires pour gérer les URLs d'images

/**
 * Normalise une URL d'image pour affichage correct
 * @param {string} imageUrl - URL de l'image à normaliser
 * @returns {string|null} - URL normalisée ou null
 */
export const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') return null;

  const trimmed = imageUrl.trim();

  // Si c'est déjà une URL complète valide
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    const id = extractGoogleDriveId(trimmed);

    // 🔹 Si c'est une URL Google Drive → utiliser un lien d'affichage stable
    if (id) {
      // uc?export=view est généralement plus fiable que thumbnail pour éviter certains 400
      return `https://drive.google.com/uc?export=view&id=${id}`;
    }

    // 🔹 Sinon, renvoyer telle quelle
    return trimmed;
  }

  // 🔹 Si c'est un chemin local (uploads/images/xxx.jpg)
  const normalizedPath = trimmed.replace(/\\/g, '/');
  const path = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  return `http://localhost:5000${path}`;
};

/**
 * Extrait l'ID Google Drive d'une URL
 * @param {string} imageUrl - URL de l'image
 * @returns {string|null} - ID Google Drive ou null
 */
export const extractGoogleDriveId = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return null;
  }

  const raw = imageUrl.trim();
  
  // Différents patterns pour extraire l'ID Google Drive
  const patterns = [
    /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
    /uc\?export=view&id=([a-zA-Z0-9_-]+)/,
    /thumbnail\?id=([a-zA-Z0-9_-]+)/,
    /^([a-zA-Z0-9_-]{25,})$/
  ];
  
  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

/**
 * Vérifie si une URL est une URL Google Drive
 * @param {string} imageUrl - URL de l'image
 * @returns {boolean} - true si c'est une URL Google Drive
 */
export const isGoogleDriveUrl = (imageUrl) => {
  return extractGoogleDriveId(imageUrl) !== null;
};

/**
 * Obtient l'URL de l'image principale d'un produit
 * @param {Object} product - Objet produit
 * @returns {string|null} - URL de l'image principale ou null
 */
export const getProductMainImage = (product) => {
  if (!product) return null;
  
  // 🔹 CORRECTION: Chercher dans cet ordre de priorité
  let mainImage = null;
  
  // 1. D'abord images.main (ce que l'import enregistre en base)
  if (product.images?.main) {
    mainImage = product.images.main;
  }
  // 2. Ensuite imageUrl (compat si présent côté FE seulement)
  else if (product.imageUrl) {
    mainImage = product.imageUrl;
  }
  // 3. Puis la galerie
  else if (product.images?.gallery && product.images.gallery.length > 0) {
    mainImage = product.images.gallery[0];
  }
  // 4. Ne plus construire de chemin local depuis filename
  //    On n'utilise pas `/uploads/images/${filename}` pour éviter les répétitions
  //    et forcer l'usage des liens Google Drive uniquement.
  
  // 🔹 IMPORTANT: Normaliser l'URL avant de la retourner
  return mainImage ? normalizeImageUrl(mainImage) : null;
};

/**
 * URL de l'image placeholder par défaut
 */
// Pas d'image placeholder locale
export const PLACEHOLDER_IMAGE_URL = '';

/**
 * Gère l'erreur de chargement d'image
 * @param {Event} event - Événement d'erreur
 */
export const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  // Masquer l'image en cas d'échec au lieu d'utiliser un placeholder local
  event.currentTarget.style.display = 'none';
};